import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
  Card,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge,
  TablePagination,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Receipt,
  VerifiedUser,
  CheckCircle,
  WatchLater,
  History,
  Refresh,
  FileCopy,
  Close,
  AccountBalance,
} from "@material-ui/icons";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";

// ─── STYLES ────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #1A3C6E 0%, #2E75B6 100%)",
    borderRadius: theme.spacing(2),
    color: "#fff",
    boxShadow: "0 8px 32px rgba(26, 60, 110, 0.3)",
  },
  headerTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerSubtitle: {
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
  },
  tabs: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: "3px 3px 0 0",
    },
  },
  tabLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 500,
  },
  badge: {
    marginLeft: theme.spacing(1),
    "& .MuiBadge-badge": {
      fontSize: "0.7rem",
      height: 20,
      minWidth: 20,
      borderRadius: 10,
    },
  },
  card: {
    borderRadius: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
    },
    marginBottom: theme.spacing(2),
    cursor: "pointer",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #f0f0f0",
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  statusChip: {
    fontWeight: 600,
    padding: theme.spacing(0.5, 1.5),
    "&.pending_approval": {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
    "&.approved": {
      backgroundColor: "#e3f2fd",
      color: "#0d47a1",
    },
    "&.rejected": {
      backgroundColor: "#fbe9e7",
      color: "#c62828",
    },
    "&.paid": {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #f5f5f5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoLabel: {
    color: "#666",
    fontWeight: 500,
  },
  infoValue: {
    fontWeight: 500,
    color: "#333",
    textAlign: "right",
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  historyTimeline: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: "#e0e0e0",
    },
  },
  historyItem: {
    position: "relative",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: -7,
      top: 16,
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: "#667eea",
      border: "2px solid #fff",
    },
    "&.approved::before": {
      backgroundColor: "#4caf50",
    },
    "&.rejected::before": {
      backgroundColor: "#f44336",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: "#999",
  },
  loadingOverlay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
}));

const STATUS_LABELS = {
  pending_approval: "PENDING APPROVAL",
  approved: "APPROVED",
  rejected: "REJECTED",
  paid: "PAID",
};

const formatLabel = (value) =>
  (value || "")
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────
// Read-only status view of misc-payment requests, from the raiser's point of
// view: pending / approved / rejected / paid. Privileged viewers (ContactConfig,
// key: VIEW_ALL_RAISED_RECORDS) see every raiser's payments; everyone else
// sees only what they personally raised. No approve/reject or edit here —
// approving happens on the finance/approver dashboard.
const PaymentRaisedStatusView = function (props) {
  const classes = useStyles();
  const [loading, setLocalLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewerIsPrivileged, setViewerIsPrivileged] = useState(false);

  const DEFAULT_PAGE_SIZE = 200;
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [historyTotal, setHistoryTotal] = useState(0);

  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
    total: 0,
  });

  // ── Fetch pending (raised) payments ─────────────────────────────────────
  const fetchPendingRecords = useCallback(async () => {
    try {
      setLocalLoading(true);
      const res = await HttpService.getRaisedPaymentsPending({
        page: pendingPage + 1,
        limit: pendingRowsPerPage,
      });
      const data = res?.data?.data?.payments || [];
      setPendingRecords(data);
      setPendingTotal(res?.data?.data?.pagination?.total ?? data.length);
      if (typeof res?.data?.data?.viewerIsPrivileged === "boolean") {
        setViewerIsPrivileged(res.data.data.viewerIsPrivileged);
      }
    } catch (err) {
      props.setSnackInfo("Failed to fetch pending payments", "error");
    } finally {
      setLocalLoading(false);
    }
  }, [pendingPage, pendingRowsPerPage]);

  // ── Fetch approved/rejected/paid history ────────────────────────────────
  const fetchHistoryRecords = useCallback(async () => {
    try {
      setLocalLoading(true);
      const res = await HttpService.getRaisedPaymentsHistory({
        page: historyPage + 1,
        limit: historyRowsPerPage,
      });
      const data = res?.data?.data?.payments || [];
      setHistoryRecords(data);
      setHistoryTotal(res?.data?.data?.pagination?.total ?? data.length);
      const statusCounts = res?.data?.data?.statusCounts;
      if (statusCounts) {
        setStats((s) => ({
          ...s,
          approved: statusCounts.approved || 0,
          rejected: statusCounts.rejected || 0,
          paid: statusCounts.paid || 0,
        }));
      }
    } catch (err) {
      props.setSnackInfo("Failed to fetch payment history", "error");
    } finally {
      setLocalLoading(false);
    }
  }, [historyPage, historyRowsPerPage]);

  useEffect(() => {
    fetchPendingRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPage, pendingRowsPerPage]);

  useEffect(() => {
    fetchHistoryRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage, historyRowsPerPage]);

  useEffect(() => {
    setStats((s) => ({ ...s, pending: pendingTotal, total: pendingTotal + historyTotal }));
  }, [pendingTotal, historyTotal]);

  // ─── View Record Details ──────────────────────────────────────────────
  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setDialogOpen(true);
  };

  // ─── Documents matching a set of doc_types, rendered as open-in-new-tab links ──
  const renderDocLinks = (record, docTypes, emptyLabel = "No documents uploaded") => {
    const docs = (record?.documents || []).filter((d) => docTypes.includes(d.doc_type));
    if (!docs.length) {
      return <Typography variant="caption" color="textSecondary">{emptyLabel}</Typography>;
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {docs.map((d, i) => (
          <Chip
            key={i}
            size="small"
            icon={<FileCopy style={{ fontSize: 14 }} />}
            label={d.original_name || d.doc_type}
            component="a"
            href={d.file_path}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            style={{ maxWidth: 220 }}
          />
        ))}
      </div>
    );
  };

  const getPayeeName = (record) =>
    record?.linked_owner?.owner_name ||
    record?.linked_service_provider?.name ||
    record?.payee_name ||
    "N/A";

  // ─── Render Record Card ────────────────────────────────────────────────
  const renderRecordCard = (record) => (
    <Card className={classes.card} onClick={() => handleViewRecord(record)}>
      <div className={classes.cardHeader}>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {getPayeeName(record)}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
            <Chip size="small" label={formatLabel(record.payment_type)} variant="outlined" style={{ fontSize: "0.7rem" }} />
            <Chip
              size="small"
              label={record.experienceCenterId?.name || "No EC"}
              variant="outlined"
              style={{ fontSize: "0.7rem" }}
            />
            <Chip
              size="small"
              label={STATUS_LABELS[record.status] || formatLabel(record.status)}
              className={`${classes.statusChip} ${record.status}`}
            />
            {viewerIsPrivileged && (
              <Chip size="small" label={`Raised by: ${record.raised_by?.name || "Unknown"}`} variant="outlined" style={{ fontSize: "0.7rem" }} />
            )}
          </Box>
        </Box>
        <Typography variant="h6" style={{ fontWeight: 700, color: "#1A3C6E" }}>
          ₹{record.net_payable ?? record.amount}
        </Typography>
      </div>
      <div className={classes.cardContent}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="textSecondary">Payment Month</Typography>
            <Typography variant="body2">{record.payment_period_label || record.payment_month || "—"}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="textSecondary">Raised On</Typography>
            <Typography variant="body2">
              {record.created_at ? new Date(record.created_at).toLocaleDateString() : "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="textSecondary">Payment Mode</Typography>
            <Typography variant="body2">{formatLabel(record.payment_mode)}</Typography>
          </Grid>
        </Grid>
      </div>
    </Card>
  );

  // ─── Render Detail Dialog ──────────────────────────────────────────────
  const renderDialog = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" style={{ fontWeight: 600 }}>
              {getPayeeName(selectedRecord)}
            </Typography>
            <Chip
              size="small"
              label={STATUS_LABELS[selectedRecord?.status] || formatLabel(selectedRecord?.status)}
              className={`${classes.statusChip} ${selectedRecord?.status}`}
              style={{ marginTop: 4 }}
            />
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {selectedRecord && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              <Receipt fontSize="small" /> Payment Details
            </Typography>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Type</span>
              <span className={classes.infoValue}>{formatLabel(selectedRecord.payment_type)}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Experience Centre</span>
              <span className={classes.infoValue}>{selectedRecord.experienceCenterId?.name || "—"}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Amount</span>
              <span className={classes.infoValue}>₹{selectedRecord.amount}</span>
            </div>
            {(selectedRecord.gst_amount > 0 || selectedRecord.tds_amount > 0) && (
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>GST / TDS</span>
                <span className={classes.infoValue}>
                  +₹{selectedRecord.gst_amount || 0} / -₹{selectedRecord.tds_amount || 0}
                </span>
              </div>
            )}
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Net Payable</span>
              <span className={classes.infoValue}>₹{selectedRecord.net_payable ?? selectedRecord.amount}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Payment Month</span>
              <span className={classes.infoValue}>{selectedRecord.payment_period_label || selectedRecord.payment_month || "—"}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Raised By</span>
              <span className={classes.infoValue}>{selectedRecord.raised_by?.name || "—"}</span>
            </div>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Raised On</span>
              <span className={classes.infoValue}>
                {selectedRecord.created_at ? new Date(selectedRecord.created_at).toLocaleString() : "—"}
              </span>
            </div>
            {selectedRecord.description && (
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Description</span>
                <span className={classes.infoValue}>{selectedRecord.description}</span>
              </div>
            )}
            <Box mt={1.5}>
              <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Attachments
              </Typography>
              {renderDocLinks(selectedRecord, ["invoice", "screenshot", "other"])}
            </Box>

            {selectedRecord.bank && (selectedRecord.bank.account_number || selectedRecord.bank.account_name) && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  <AccountBalance fontSize="small" /> Bank Details
                </Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Account Name</span>
                  <span className={classes.infoValue}>{selectedRecord.bank.account_name || "—"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Account Number</span>
                  <span className={classes.infoValue}>{selectedRecord.bank.account_number || "—"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Bank / IFSC</span>
                  <span className={classes.infoValue}>{selectedRecord.bank.bank_name} - {selectedRecord.bank.ifsc_code}</span>
                </div>
              </>
            )}

            {(selectedRecord.status === "approved" || selectedRecord.status === "paid") && (
              <>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Bank Processing
                </Typography>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Sent to Bank</span>
                  <span className={classes.infoValue}>{selectedRecord.sentToBank ? "Yes" : "Not yet"}</span>
                </div>
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Bank Status</span>
                  <span className={classes.infoValue}>{formatLabel(selectedRecord.bankStatus)}</span>
                </div>
              </>
            )}

            {selectedRecord.approvalHistory?.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  <History fontSize="small" /> Approval History
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box className={classes.historyTimeline}>
                  {selectedRecord.approvalHistory.map((item, idx) => (
                    <div key={idx} className={`${classes.historyItem} ${item.action}`}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          Level {item.level} - {item.action?.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(item.actedAt).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        By: {item.actedBy?.name || "Unknown"}
                      </Typography>
                      {item.remarks && (
                        <Typography variant="caption" style={{ color: "#666" }}>
                          Remarks: {item.remarks}
                        </Typography>
                      )}
                    </div>
                  ))}
                </Box>
              </Box>
            )}

            {selectedRecord.revisionHistory?.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Revision History
                </Typography>
                <Divider style={{ margin: "8px 0 16px" }} />
                <Box className={classes.historyTimeline}>
                  {selectedRecord.revisionHistory.map((item, idx) => (
                    <div key={idx} className={classes.historyItem}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {formatLabel(item.revision_type)}
                          {item.revised_amount ? ` — ₹${item.revised_amount}` : ""}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {item.revisedAt ? new Date(item.revisedAt).toLocaleString() : "—"}
                        </Typography>
                      </Box>
                      {item.postponed_note && (
                        <Typography variant="caption" style={{ color: "#666" }}>
                          Note: {item.postponed_note}
                        </Typography>
                      )}
                    </div>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  // ─── Main Render ──────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      <Paper className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>
          <VerifiedUser fontSize="large" />
          Payment Requests Status
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          Track misc-payments you've raised — pending, approved, rejected and paid.
          {viewerIsPrivileged
            ? " You're on the privileged viewers list, so you're seeing every raiser's payments."
            : " Records shown are your own, unless you're on the privileged viewers list."}
        </Typography>
      </Paper>

      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Pending</Typography>
            <Typography variant="h5" style={{ color: "#ff9800", fontWeight: 600 }}>{stats.pending}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Approved</Typography>
            <Typography variant="h5" style={{ color: "#0d47a1", fontWeight: 600 }}>{stats.approved}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Rejected</Typography>
            <Typography variant="h5" style={{ color: "#f44336", fontWeight: 600 }}>{stats.rejected}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Paid</Typography>
            <Typography variant="h5" style={{ color: "#2e7d32", fontWeight: 600 }}>{stats.paid}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={8} md={4}>
          <Paper style={{ padding: 16, textAlign: "center", borderRadius: 12 }}>
            <Typography variant="caption" color="textSecondary">Total</Typography>
            <Typography variant="h5" style={{ color: "#667eea", fontWeight: 600 }}>{stats.total}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.tabs}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} indicatorColor="primary" textColor="primary" variant="fullWidth">
          <Tab
            label={
              <div className={classes.tabLabel}>
                <WatchLater fontSize="small" />
                Pending
                <Badge badgeContent={stats.pending} color="secondary" className={classes.badge} />
              </div>
            }
          />
          <Tab
            label={
              <div className={classes.tabLabel}>
                <History fontSize="small" />
                History
                <Badge badgeContent={stats.approved + stats.rejected + stats.paid} color="primary" className={classes.badge} />
              </div>
            }
          />
        </Tabs>
      </Paper>

      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => {
            fetchPendingRecords();
            fetchHistoryRecords();
          }}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {tabValue === 0 && (
            <>
              {pendingRecords.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <CheckCircle style={{ fontSize: 60, color: "#4caf50", marginBottom: 16 }} />
                  <Typography variant="h6">No pending payments</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Nothing waiting on approval right now.
                  </Typography>
                </Paper>
              ) : (
                <>
                  {pendingRecords.map((item) => renderRecordCard(item))}
                  <Paper style={{ borderRadius: 12 }}>
                    <TablePagination
                      component="div"
                      count={pendingTotal}
                      page={pendingPage}
                      onChangePage={(e, newPage) => setPendingPage(newPage)}
                      rowsPerPage={pendingRowsPerPage}
                      onChangeRowsPerPage={(e) => {
                        setPendingRowsPerPage(parseInt(e.target.value, 10));
                        setPendingPage(0);
                      }}
                      rowsPerPageOptions={[50, 100, 200, 500]}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
          {tabValue === 1 && (
            <>
              {historyRecords.length === 0 ? (
                <Paper className={classes.emptyState}>
                  <History style={{ fontSize: 60, color: "#999", marginBottom: 16 }} />
                  <Typography variant="h6">No history available</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Approved, rejected and paid payments will appear here.
                  </Typography>
                </Paper>
              ) : (
                <>
                  {historyRecords.map((item) => renderRecordCard(item))}
                  <Paper style={{ borderRadius: 12 }}>
                    <TablePagination
                      component="div"
                      count={historyTotal}
                      page={historyPage}
                      onChangePage={(e, newPage) => setHistoryPage(newPage)}
                      rowsPerPage={historyRowsPerPage}
                      onChangeRowsPerPage={(e) => {
                        setHistoryRowsPerPage(parseInt(e.target.value, 10));
                        setHistoryPage(0);
                      }}
                      rowsPerPageOptions={[50, 100, 200, 500]}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
        </>
      )}

      {renderDialog()}
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(PaymentRaisedStatusView);
